import React, { useState, useEffect } from 'react';
import {
  KatModal,
  KatButton,
  KatTextarea,
  KatSpinner,
} from '@amzn/katal-react';
import {
  useRejectSourceReviewMutation,
  RejectSourceReviewInput,
} from 'src/generated/graphql';
import { useTranslation } from 'src/i18next-arb-shim/useTranslation';
import './RejectSourceReviewModal.scss';

interface Props {
  stringGroupName: string | null | undefined;
  operatorAlias: string | null | undefined;
  requestId: string | null | undefined;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const RejectSourceReviewModal: React.FC<Props> = (props: Props) => {
  const [rejectionReason, setRejectionReason] = useState('');
  const [hasValidationError, setHasValidationError] = useState(false);
  const { t } = useTranslation();
  const [rejectSourceReview, { loading, error, reset }] =
    useRejectSourceReviewMutation();

  const mutationInput: RejectSourceReviewInput = {
    stringGroupName: props.stringGroupName,
    operatorAlias: props.operatorAlias,
    requestId: props.requestId,
    rejectionReason: rejectionReason,
  };

  const confirmRejection = () => {
    if (!rejectionReason.trim()) {
      setHasValidationError(true);
      return;
    }

    rejectSourceReview({
      variables: {
        params: mutationInput,
      },
      errorPolicy: 'all',
    })
      .then((response) => {
        // Submission for translation success
        if (!response.errors) {
          props.onClose();
          if (props.onSuccess) {
            props.onSuccess();
          }
        }
      })
      // Submission for translation error
      .catch((error) => {
        console.error('Error rejecting the source review with error:', error);
      });
  };

  // Reset state when the modal opens or closes
  useEffect(() => {
    if (props.isOpen) {
      reset();
      setHasValidationError(false);
      setRejectionReason('');
    }
  }, [props.isOpen, reset]);

  const handleCancel = () => {
    setRejectionReason('');
    setHasValidationError(false);
    props.onClose();
  };

  if (error) {
    let errorMessage;
    if (error.message.includes('has no open source review')) {
      errorMessage = t(
        'source-review-string-group-already-submitted-display-error-message'
      );
    } else {
      errorMessage = t('source-review-server-error-message');
    }
    return (
      <KatModal
        onClose={handleCancel}
        title={t('source-review-rejection-modal-title')}
        visible={props.isOpen}
      >
        <div className="rejection-error-container">
          <div className="rejection-error-text">{errorMessage}</div>
        </div>
        <div slot="footer">
          <KatButton
            id="rejection-dismiss-button"
            label={t('source-review-rejection-cancel-button-text')}
            variant="primary"
            onClick={props.onClose}
          />
        </div>
      </KatModal>
    );
  }

  if (loading) {
    return (
      <KatModal
        noCloseIcon={true}
        visible={props.isOpen}
        className="rejection-loading-modal"
      >
        <div className="rejection-loading-container">
          <KatSpinner size="small" />
          <div className="rejection-loading-text">
            {t('source-review-rejection-in-progress-message')}
          </div>
        </div>
      </KatModal>
    );
  }

  return (
    <KatModal
      onClose={handleCancel}
      title={t('source-review-rejection-modal-title')}
      visible={props.isOpen}
    >
      <div>{t('source-review-rejection-information-text')}</div>
      <div>
        <KatTextarea
          className="rejection-reason-textarea"
          value={rejectionReason}
          placeholder={t('source-review-rejection-reason-placeholder-text')}
          onChange={(e: CustomEvent) => {
            setRejectionReason((e.target as HTMLTextAreaElement).value);
            if (hasValidationError) {
              setHasValidationError(false);
            }
          }}
        />
      </div>
      {hasValidationError && (
        <div className="rejection-reason-warning-text">
          {t('source-review-rejection-reason-warning-text')}
        </div>
      )}
      <br />
      <div slot="footer">
        <KatButton
          id="source-review-rejection-cancel-button"
          label={t('source-review-rejection-cancel-button-text')}
          variant="secondary"
          onClick={handleCancel}
          className="rejection-cancel-button"
        />
        <KatButton
          id="source-review-rejection-confirm-button"
          label={t('source-review-rejection-confirm-button-text')}
          variant="danger"
          onClick={confirmRejection}
        />
      </div>
    </KatModal>
  );
};

export default RejectSourceReviewModal;
